import { cn } from '@/lib/cn';

/**
 * Usage against a ceiling.
 *
 * The bar is an illustration of the two figures printed above it, never the
 * only statement of them: a trader deciding whether a card can still take a
 * payment needs the numbers, and a bar alone at 97% and at 100% look the same.
 *
 * `ratio` of `null` means "no ceiling" — the money layer treats 0 as unlimited,
 * and an unlimited card has nothing to fill.
 */
export function Meter({
  ratio,
  label,
  value,
  hint,
  className,
}: {
  ratio: number | null;
  /** Screen-reader name for the bar, e.g. «Использовано за сутки». */
  label: string;
  /** The figures, rendered as written. */
  value: string;
  hint?: string;
  className?: string;
}) {
  const isFull = ratio !== null && ratio >= 1;

  return (
    <div className={cn('min-w-0', className)}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <p className="eyebrow leading-snug">{label}</p>
        <p className="num text-xs text-fg">{value}</p>
      </div>

      {ratio === null ? null : (
        <div
          role="progressbar"
          aria-label={label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(ratio * 100)}
          className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-sunken"
        >
          <div
            className={cn('h-full rounded-full', isFull ? 'bg-warn-500' : 'bg-fg-secondary')}
            // Nothing used draws nothing. Above zero the fill is floored at 2%
            // so a single small payment against a large ceiling is still
            // visible rather than rounding away to an empty bar.
            style={{ width: ratio === 0 ? '0%' : `${Math.max(2, Math.round(ratio * 100))}%` }}
          />
        </div>
      )}

      {hint ? <p className="mt-1 text-2xs text-fg-muted">{hint}</p> : null}
    </div>
  );
}
